"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getObjectDiff = exports.useOnChange = void 0;
const react_1 = require("react");
const debounce_1 = __importDefault(require("lodash/debounce"));
const utils_1 = require("./utils");
const tracking_1 = require("../constants/tracking");
/**
 *
 * Hook to wrap the props.onChange handler with the handleChange function from `utils`
 * with functionality to also call props.onTrack in a debounced way per-field. The
 * new handleChangeWithTracking is returned by the hook
 *
 * @param {Object} the params used by handleChange + container props
 * @returns {Object} object with `handleChangeWithTracking` callback
 */
const useOnChange = ({ updatedFieldsRef, getErrors, props, formContext, setFormContext, renderErrors }) => {
    // only used when `props.onTrack` is passed in
    const currentFields = (0, react_1.useRef)(null);
    // we keep a debounced ontrack per-field
    const debouncedTrackCalls = (0, react_1.useRef)({});
    // get handler from util
    const handleChange = (0, utils_1.getHandleChange)({
        updatedFieldsRef,
        getErrors,
        props,
        formContext,
        setFormContext,
        renderErrors
    });
    // wrap handler with one that calls debounced track function on diff
    const handleChangeWithTracking = (payload) => {
        handleChange(payload);
        const onTrack = props.onTrack;
        if (onTrack) {
            const updatedFieldsDiff = getObjectDiff(currentFields.current, updatedFieldsRef.current);
            currentFields.current = updatedFieldsRef.current;
            Object.keys(updatedFieldsDiff).forEach((key) => {
                // each field (by id) has its own debounced track function, instead of just one
                // to make sure we capture each updated field. If we had one, we might lose a specific field id
                // if the user moves on and updates a different id faster than the debounce time
                const trackField = (debouncedTrackCalls.current[key] =
                    debouncedTrackCalls.current[key] ||
                        (0, debounce_1.default)(onTrack, tracking_1.TRACKING_DEBOUNCE_MS));
                trackField(tracking_1.USER_ACTION_FIELD_UPDATED, {
                    field_name: key,
                    field_value: updatedFieldsDiff[key]
                });
            });
        }
    };
    return {
        handleChangeWithTracking
    };
};
exports.useOnChange = useOnChange;
/**
 * Util function to get the object of differences between two sets of form values.
 * forms-v2 always sends down the entire form object in the `handleChange` event,
 * this function will get the fields that have changed.
 * Values that were unset, or changed to null/'', will be omitted from the diff object
 *
 * @param {*} obj1 Object of form field values keyed by element id, null on first field edit
 * @param {*} obj2 [updated] Object of form field values keyed by element id
 * @returns {Object} Object of updated fields between obj1 and obj2 keyed by element id
 */
function getObjectDiff(obj1, obj2) {
    const updatedFields = {};
    // add fields in obj2 that have changed since obj1
    Object.keys(obj2).forEach((fieldName) => {
        const obj2Value = obj2[fieldName].value;
        // if field exists and old fields and has been updated, add it to the updatedFields
        if (obj1 && fieldName in obj1) {
            const obj1Value = obj1[fieldName].value;
            if (obj2Value && obj2Value !== obj1Value) {
                updatedFields[fieldName] = obj2Value;
            }
        }
        else if (obj2Value) {
            // if it's a new field, add it to the updatedFields
            updatedFields[fieldName] = obj2Value;
        }
    });
    return updatedFields;
}
exports.getObjectDiff = getObjectDiff;
//# sourceMappingURL=use-on-change.js.map